import PropTypes from "prop-types";
import {
  Card,
  CardPreview,
  CollectionCardPreview,
  Content,
  Text,
  Image,
  Badge,
  ActionMenu,
  MenuItem,
  TooltipTrigger,
  Tooltip,
} from "@react-spectrum/s2";
import Copy from "@react-spectrum/s2/icons/Copy";
import Share from "@react-spectrum/s2/icons/Share";
import { TemplateIcon } from "./TemplateIcon";
import { useHover } from "@react-aria/interactions";

import { S3Image } from "@app/components/S3Image";
import { getRelativeTime } from "@app/utils";

import { style, size } from "@react-spectrum/s2/style" with { type: "macro" };

const PROJECT_STATUS_CONFIG = [
  {
    condition: (item) =>
      typeof item.status === "undefined" && !item?.videoPath && !item?.jobId,
    variant: "yellow",
    label: "Draft",
  },
  {
    condition: (item) =>
      item.status === "processing" || (!item?.videoPath && !!item?.jobId),
    variant: "informative",
    label: "Processing",
  },
  {
    condition: (item) =>
      (item.status === "completed" || !!item?.videoPath) &&
      Date.now() - new Date(item.updatedAt) < 24 * 60 * 60 * 1000,
    variant: "positive",
    label: "New",
  },
];

const ProjectStatusBadge = ({ item }) => {
  const status = PROJECT_STATUS_CONFIG.find(({ condition }) => condition(item));

  if (!status) return null;

  return (
    <Badge
      variant={status.variant}
      size="XS"
      styles={style({
        position: "absolute",
        bottom: 8,
        insetEnd: 8,
      })}
    >
      {status.label}
    </Badge>
  );
};

ProjectStatusBadge.propTypes = {
  item: PropTypes.shape({
    videoPath: PropTypes.string,
    jobId: PropTypes.string,
    updatedAt: PropTypes.string.isRequired,
  }).isRequired,
};

export const ProjectsListCard = ({
  item,
  onPress,
  onMenuAction,
  isDuplicating = false,
}) => {
  const { hoverProps, isHovered } = useHover({});

  let template;
  if (["mogrt", "modular"].includes(item.templateType)) {
    template = {
      label: "After Effects",
      type: "AE",
    };
  } else if (["figma", "html"].includes(item.templateType)) {
    template = {
      label: "Figma",
      type: "Figma",
    };
  } else {
    template = {
      label: "Adobe Express",
      type: "Express",
    };
  }
  const templateLabel = template.label;

  return (
    <Card
      {...hoverProps}
      id={item.id}
      variant="quiet"
      textValue={item.name}
      onPress={onPress}
    >
      <div
        className={style({
          position: "relative",
        })}
      >
        <CollectionCardPreview>
          <S3Image
            src={item?.rendition_grid?.main?.presignURL}
            filePath={item?.rendition_grid?.main?.filePath}
            alt={item?.rendition_grid?.main?.name ?? item.name ?? ""}
            styles={style({
              objectFit: "contain",
              padding: 8,
              boxSizing: "border-box",
            })}
          />
          {!!item?.rendition_grid?.tiles?.length &&
            item?.rendition_grid?.tiles?.map((tile) => (
              <S3Image
                key={tile.name}
                src={tile?.presignURL}
                filePath={tile?.filePath}
                alt={tile?.name ?? ""}
              />
            ))}
          {new Array(3 - (item?.rendition_grid?.tiles?.length || 0))
            .fill(null)
            .map((_, index) => (
              <S3Image key={index} alt="" />
            ))}
        </CollectionCardPreview>

        <ProjectStatusBadge item={item} />

        {onMenuAction && (
          <ActionMenu
            aria-label={`Actions for ${item.name}`}
            onAction={(action) => onMenuAction(action, item)}
            isQuiet={false}
            staticColor="auto"
            styles={style({
              position: "absolute",
              top: 32,
              insetEnd: 8,
            })}
          >
            <MenuItem id="share">
              <Share />
              <Text>Share</Text>
            </MenuItem>
            <MenuItem id="duplicate" isDisabled={isDuplicating}>
              <Copy />
              <Text>{isDuplicating ? "Duplicating..." : "Duplicate"}</Text>
            </MenuItem>
          </ActionMenu>
        )}
      </div>
      <Content styles={style({ alignContent: "start" })}>
        <Text slot="title">{item.name}</Text>
        <Text
          slot="description"
          styles={style({
            display: "flex",
            alignItems: "start",
          })}
        >
          <TemplateIcon
            type={template.type}
            styles={style({
              size: 16,
              marginEnd: {
                isFigmaType: 2,
                default: size(5),
              },
            })({ isFigmaType: template.type === "Figma" })}
          />
          {template.label}
          <span className={style({ marginX: 4 })}>•</span>
          {getRelativeTime(item.updatedAt)}
        </Text>
      </Content>
    </Card>
  );
};

ProjectsListCard.propTypes = {
  onMenuAction: PropTypes.func,
  isDuplicating: PropTypes.bool,
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    templateType: PropTypes.oneOf([
      "mogrt",
      "modular",
      "express",
      "figma",
      "html",
    ]).isRequired,
    name: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    thumbnail: PropTypes.shape({
      filePath: PropTypes.string,
      presignURL: PropTypes.string,
    }),
    rendition_grid: PropTypes.shape({
      main: PropTypes.shape({
        name: PropTypes.string,
        filePath: PropTypes.string,
        presignURL: PropTypes.string,
      }),
      tiles: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          filePath: PropTypes.string,
          presignURL: PropTypes.string,
        }),
      ),
    }),
    expressId: PropTypes.string,
    videoPath: PropTypes.string,
    jobId: PropTypes.string,
  }).isRequired,
  onPress: PropTypes.func,
};
